import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, FilePlus2, Users, LogOut, Package, Building2, PackageCheck } from "lucide-react";

const NavItem = ({ to, icon: Icon, children, testId }) => (
  <NavLink
    to={to}
    end
    data-testid={testId}
    className={({ isActive }) =>
      `flex items-center gap-3 rounded-sm px-3 py-2 text-sm transition-colors ${
        isActive
          ? "bg-[#0F2537] text-white"
          : "text-slate-700 hover:bg-slate-100"
      }`
    }
  >
    <Icon className="h-4 w-4" strokeWidth={1.75} />
    {children}
  </NavLink>
);

const AppLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const isPrint = loc.pathname.match(/\/quotes\/[^/]+$/) ||
    (loc.pathname.match(/^\/packing-lists\/[^/]+$/) && !loc.pathname.endsWith("/new"));

  return (
    <div className="flex min-h-screen bg-[#FAFAFA]">
      <aside className="no-print w-60 shrink-0 border-r border-[#E5E7EB] bg-white">
        <div className="flex h-16 items-center gap-2 border-b border-[#E5E7EB] px-4">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-2">
            <img src="/logo.png" alt="Excellent Plastic" className="h-9 w-9 object-contain" />
            <div className="leading-tight">
              <div className="font-heading text-[13px] font-extrabold uppercase tracking-tight text-[#1E40AF]">Excellent Plastic</div>
              <div className="text-[9px] uppercase tracking-widest text-slate-500">Quote Manager</div>
            </div>
          </Link>
        </div>
        <nav className="flex flex-col gap-1 p-3">
          <NavItem to="/" icon={LayoutDashboard} testId="nav-dashboard">Dashboard</NavItem>
          <NavItem to="/quotes/new" icon={FilePlus2} testId="nav-new-quote">New Quote</NavItem>
          <NavItem to="/products" icon={Package} testId="nav-products">Data Sheets</NavItem>
          <NavItem to="/packing-lists" icon={PackageCheck} testId="nav-packing-lists">Packing Lists</NavItem>
          {user?.role === "admin" && (
            <NavItem to="/customers" icon={Building2} testId="nav-customers">Customers</NavItem>
          )}
          {user?.role === "admin" && (
            <NavItem to="/users" icon={Users} testId="nav-users">Users</NavItem>
          )}
        </nav>
        <div className="absolute bottom-0 w-60 border-t border-[#E5E7EB] bg-white p-3">
          <div className="mb-2 rounded-sm bg-slate-50 px-3 py-2">
            <div className="text-xs text-slate-500">Signed in as</div>
            <div className="truncate text-sm font-medium ep-text-primary" data-testid="current-user-name">{user?.name}</div>
            <div className="truncate text-xs text-slate-500" data-testid="current-user-email">{user?.email}</div>
            <div className="mt-1 inline-block rounded-sm bg-[#0F2537] px-2 py-0.5 text-[10px] uppercase tracking-wider text-white">
              {user?.role}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full rounded-sm"
            onClick={logout}
            data-testid="logout-button"
          >
            <LogOut className="mr-2 h-4 w-4" /> Logout
          </Button>
        </div>
      </aside>
      <main className={`flex-1 ${isPrint ? "" : "p-6 md:p-8"}`}>
        {children}
      </main>
    </div>
  );
};

export default AppLayout;
